import { useState } from 'react'
import useMenuBack from '../../hooks/useMenuBack'
import { ArrowLeft, Eye, EyeOff, Check } from 'lucide-react'
import { ResponsiveWrapper } from '../../components/layout'
import { authAPI } from '../../services/api'
import { useI18n } from '../../i18n'

/**
 * ChangePasswordScreen v238.20.0 — redesigned to match the security screens.
 * Compact header, rounded card inputs, and a live password-requirements checklist
 * (8+ characters, a letter, a number) mirroring the signup policy.
 */

// Module-scope so its identity is stable across renders (keeps input focus).
function PasswordField({ label, value, onChange, show, setShow }) {
  return (
    <div>
      <label className="block text-sm text-gray-500 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500 pr-12"
        />
        <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
          {show ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>
    </div>
  )
}

export default function ChangePasswordScreen() {
  const goBack = useMenuBack()
  const { t } = useI18n()

  const [formData, setFormData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' })
  const [showOld, setShowOld] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [othersSignedOut, setOthersSignedOut] = useState(false)
  const [saving, setSaving] = useState(false)

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    setError('')
  }

  // Password policy — same as signup: 8+ chars, a letter, a number.
  const reqs = [
    { met: formData.newPassword.length >= 8, label: t('auth.pwReqLength', '8+ characters') },
    { met: /[A-Za-z]/.test(formData.newPassword), label: t('auth.pwReqLetter', 'A letter') },
    { met: /\d/.test(formData.newPassword), label: t('auth.pwReqNumber', 'A number') },
  ]
  const pwValid = reqs.every((r) => r.met)
  const pwMatch = formData.confirmPassword.length > 0 && formData.newPassword === formData.confirmPassword
  const isValid = formData.oldPassword.length >= 1 && pwValid && pwMatch

  const handleSubmit = async () => {
    if (!isValid) {
      if (!pwValid) setError(t('auth.passwordRequirements', 'Password must be at least 8 characters and include a letter and a number.'))
      else if (!pwMatch) setError(t('security.passwordsDoNotMatch', 'Passwords do not match'))
      return
    }
    setSaving(true)
    setError('')
    try {
      const result = await authAPI.changePassword(formData.oldPassword, formData.newPassword)
      setSaving(false)
      // PROD-1349: only an API that ended the other sessions answers with a token; an older API shows no notice.
      const signedOut = !!(result && result.token)
      setOthersSignedOut(signedOut)
      setSuccess(true)
      setTimeout(() => { goBack() }, signedOut ? 4000 : 2000)
    } catch (e) {
      setSaving(false)
      setError(e.error || t('security.changePasswordFailed', 'Could not change password. Check your current password and try again.'))
    }
  }

  if (success) {
    return (
      <ResponsiveWrapper className="bg-white shadow-xl">
        <div className="screen-min-h bg-white flex flex-col items-center justify-center p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-green-600" strokeWidth={2.5} />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('security.passwordUpdated', 'Password updated')}</h2>
          <p className="text-gray-500 text-center">{t('security.yourPasswordHasBeenChanged', 'Your password has been changed.')}</p>
          {othersSignedOut && (
            <p className="text-gray-500 text-sm text-center mt-3">{t('security.signedOutOtherDevices')}</p>
          )}
        </div>
      </ResponsiveWrapper>
    )
  }

  return (
    <ResponsiveWrapper className="bg-white shadow-xl">
    <div className="screen-min-h bg-white flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => goBack()} className="p-2 -ml-2 text-gray-600">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold text-orella-700">{t('security.changePassword', 'Change password')}</h1>
      </div>

      <div className="flex-1 px-4 pb-8 overflow-auto">
        <p className="text-sm text-gray-500 mt-1 mb-5">{t('security.changeYourPassword', 'Change your password.')}</p>

        <div className="space-y-4">
          <PasswordField
            label={t('security.oldPassword', 'Old password')}
            value={formData.oldPassword}
            onChange={handleChange('oldPassword')}
            show={showOld}
            setShow={setShowOld}
          />

          <div>
            <PasswordField
              label={t('security.newPassword', 'New password')}
              value={formData.newPassword}
              onChange={handleChange('newPassword')}
              show={showNew}
              setShow={setShowNew}
            />
            {formData.newPassword.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-x-3.5 gap-y-1.5">
                {reqs.map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-xs" style={{ color: r.met ? '#2A8A38' : '#94A3B8' }}>
                    <span className="inline-flex items-center justify-center" style={{ width: 15, height: 15, borderRadius: 999, background: r.met ? '#66D170' : '#E2E8F0' }}>
                      {r.met && <Check size={10} className="text-white" strokeWidth={3.5} />}
                    </span>
                    {r.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <PasswordField
              label={t('security.confirmPassword', 'Confirm password')}
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              show={showConfirm}
              setShow={setShowConfirm}
            />
            {formData.confirmPassword.length > 0 && !pwMatch && (
              <p className="text-red-500 text-xs mt-1.5">{t('security.passwordsDoNotMatch', 'Passwords do not match')}</p>
            )}
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
      </div>

      {/* Bottom buttons */}
      <div className="p-4 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={!isValid || saving}
          className={`w-full py-3.5 rounded-full font-semibold text-sm ${isValid && !saving ? 'bg-orella-500 text-white' : 'bg-gray-200 text-gray-400'}`}
        >
          {saving ? t('security.saving', 'Saving…') : t('security.changePassword', 'Change password')}
        </button>
        <button
          onClick={() => goBack()}
          className="w-full py-3.5 bg-white border border-gray-200 rounded-full font-semibold text-sm text-gray-600"
        >
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </div>
    </ResponsiveWrapper>
  )
}
